import { useState, useEffect, useCallback, useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  ValkyrieSqsQueueListEditor,
  getQueuesReferencedByLambdas,
  getValkyrieRemovedQueues,
} from 'config-editor';
import { Alert } from '@material-ui/lab';
import { parseJsonField } from '../../utils';
import type { SqsQueue, ValkyrieLambda } from 'config-editor-common';

type Props = FieldExtensionComponentProps<SqsQueue[]>;

export const ValkyrieSqsQueueEditor = (props: Props) => {
  const { onChange, schema, formData, formContext } = props;

  const rootFormData = (formContext as { formData?: Record<string, unknown> })?.formData;
  const definedQueueNames = useMemo(() => {
    const queuesData = formData ?? rootFormData?.sqs_queues ?? [];
    const qs = Array.isArray(queuesData) ? queuesData : [];
    return new Set((qs as SqsQueue[]).map(q => q.name).filter(Boolean) as string[]);
  }, [formData, rootFormData?.sqs_queues]);
  const endpointLambdas = useMemo(() => {
    const el = rootFormData?.endpoint_lambdas;
    if (el && typeof el === 'object' && !Array.isArray(el)) {
      return (
        (el as { lambdas?: Record<string, ValkyrieLambda> }).lambdas ??
        (el as Record<string, ValkyrieLambda>)
      );
    }
    return {} as Record<string, ValkyrieLambda>;
  }, [rootFormData?.endpoint_lambdas]);
  const queueConsumerLambdas = useMemo(
    () => (rootFormData?.queue_consumer_lambdas ?? {}) as Record<string, ValkyrieLambda>,
    [rootFormData?.queue_consumer_lambdas],
  );
  const removedQueues = useMemo(() => {
    const referenced = getQueuesReferencedByLambdas(endpointLambdas, queueConsumerLambdas);
    return getValkyrieRemovedQueues(referenced, definedQueueNames);
  }, [endpointLambdas, queueConsumerLambdas, definedQueueNames]);

  const getInitialValue = useCallback((): SqsQueue[] => {
    if (formData === undefined || formData === null) {
      if (schema.default !== undefined) {
        return parseJsonField(schema.default, [] as SqsQueue[]);
      }
      return [] as SqsQueue[];
    }
    return parseJsonField(formData, [] as SqsQueue[]);
  }, [formData, schema]);

  const [queues, setQueues] = useState<SqsQueue[]>(() => getInitialValue());

  useEffect(() => {
    setQueues(getInitialValue());
  }, [getInitialValue]);

  return (
    <>
      {removedQueues.length > 0 && (
        <Alert severity="warning" style={{ marginBottom: 16 }}>
          Lambdas reference queue(s) not in the list below:{' '}
          <strong>{removedQueues.join(', ')}</strong>. Add them here or update the lambdas in the
          Lambdas Configuration section.
        </Alert>
      )}
      <ValkyrieSqsQueueListEditor
        queues={queues}
        onChange={newQueues => {
          setQueues(newQueues);
          onChange(newQueues);
        }}
      />
    </>
  );
};
